import React from "react";
import { useIntl } from "react-intl";
import { PeekChip, PeekStack, PeekTooltip, PeekTypography, useTheme } from "@piiqtechnologies/ui-components";
import { UnderlinedTooltip } from "@components/common/underlined-tooltip";
import quadrantMessages from "./messages.i18n";
import { CartesianGrid, Customized, ReferenceArea, ReferenceLine, ResponsiveContainer, Scatter, ScatterChart, XAxis, YAxis, } from "recharts";
import { Ellipse } from "@piiqtechnologies/svg-icons";
import { CLUSTER_RADIUS_PX, MIN_CLUSTER_POINTS, LABEL_CHAR_WIDTH_PX, assignLabelSides, clusterByPixelDistance, truncateLabel, } from "./chartLayout";
const MARKER_RADIUS = 4;
const LABEL_GAP = 6;
const BADGE_BOX_W = 72;
const BADGE_BOX_H = 34;
export const quadrantChartQuadrants = ({ xDomain, yDomain, splitX, splitY, }) => {
    const theme = useTheme();
    const intl = useIntl();
    return [
        {
            id: "monitor",
            title: "Monitor",
            xRange: [xDomain[0], splitX],
            yRange: [splitY, yDomain[1]],
            fill: theme.palette.blue[200],
            titleColor: theme.palette.blue[500],
            tooltip: intl.formatMessage(quadrantMessages.tooltipMonitor),
        },
        {
            id: "maintain",
            title: "Maintain",
            xRange: [splitX, xDomain[1]],
            yRange: [splitY, yDomain[1]],
            fill: theme.palette.success.light,
            titleColor: theme.palette.success.main,
            tooltip: intl.formatMessage(quadrantMessages.tooltipMaintain),
        },
        {
            id: "improve",
            title: "Improve",
            xRange: [xDomain[0], splitX],
            yRange: [yDomain[0], splitY],
            fill: theme.palette.warning.light,
            titleColor: theme.palette.warning.main,
            tooltip: intl.formatMessage(quadrantMessages.tooltipImprove),
        },
        {
            id: "priority-fix",
            title: "Priority Fix",
            xRange: [splitX, xDomain[1]],
            yRange: [yDomain[0], splitY],
            fill: theme.palette.error.light,
            titleColor: theme.palette.error.main,
            tooltip: intl.formatMessage(quadrantMessages.tooltipPriorityFix),
        },
    ];
};
const ClusterTooltip = ({ members, getPointColor, onHover, onClick }) => (React.createElement(PeekStack, { gap: 0.5, sx: { minWidth: 180, maxWidth: 280 } }, members.map((member) => {
    const color = getPointColor(member);
    return (React.createElement(PeekStack, { key: member.name, direction: "row", alignItems: "center", gap: 0.75, onMouseEnter: () => onHover === null || onHover === void 0 ? void 0 : onHover(member), onMouseLeave: () => onHover === null || onHover === void 0 ? void 0 : onHover(undefined), onClick: () => onClick === null || onClick === void 0 ? void 0 : onClick(member), sx: {
            px: 0.5,
            py: 0.25,
            borderRadius: 0.5,
            cursor: onClick ? "pointer" : "default",
            "&:hover": { backgroundColor: "grey.50" },
        } },
        React.createElement(Ellipse, { color: color }),
        React.createElement(PeekTypography, { variant: "body2", sx: { flex: 1, minWidth: 0, whiteSpace: "nowrap", color: "text.primary" } }, member.name)));
})));
const dominantColor = (colors) => {
    const counts = colors.reduce((acc, color) => {
        var _a;
        acc[color] = ((_a = acc[color]) !== null && _a !== void 0 ? _a : 0) + 1;
        return acc;
    }, {});
    return Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];
};
const CLUSTER_LABELS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
const ClusterLayer = ({ points, clusterRadiusPx, minClusterPoints, onClusters, getPointColor, activePointName, onPointHover, onPointClick, xAxisMap, yAxisMap, }) => {
    var _a, _b;
    const theme = useTheme();
    const xScale = xAxisMap ? (_a = Object.values(xAxisMap)[0]) === null || _a === void 0 ? void 0 : _a.scale : undefined;
    const yScale = yAxisMap ? (_b = Object.values(yAxisMap)[0]) === null || _b === void 0 ? void 0 : _b.scale : undefined;
    if (!xScale || !yScale)
        return null;
    const positions = points.map((point) => ({ x: xScale(point.x), y: yScale(point.y) }));
    const { clusters, singletons } = clusterByPixelDistance(positions, clusterRadiusPx, undefined, minClusterPoints);
    const labelFor = (index) => { var _a; return (_a = CLUSTER_LABELS[index]) !== null && _a !== void 0 ? _a : String(index + 1); };
    if (onClusters) {
        onClusters(clusters.map((cluster, index) => ({
            label: labelFor(index),
            names: cluster.members.map((member) => points[member].name),
        })));
    }
    const labelSides = assignLabelSides(singletons.map((index) => ({
        x: positions[index].x,
        y: positions[index].y,
        halfWidth: (truncateLabel(points[index].name).length * LABEL_CHAR_WIDTH_PX) / 2,
    })));
    return (React.createElement(React.Fragment, null,
        singletons.map((index, order) => {
            const point = points[index];
            const { x: cx, y: cy } = positions[index];
            const color = getPointColor(point);
            const isActive = point.name === activePointName;
            const above = labelSides[order] === "above";
            const label = truncateLabel(point.name);
            const marker = (React.createElement("g", { onMouseEnter: () => onPointHover === null || onPointHover === void 0 ? void 0 : onPointHover(point), onMouseLeave: () => onPointHover === null || onPointHover === void 0 ? void 0 : onPointHover(undefined), onClick: () => onPointClick === null || onPointClick === void 0 ? void 0 : onPointClick(point), style: { cursor: onPointClick ? "pointer" : "default" } },
                React.createElement("circle", { className: "quadrant-point-marker", cx: cx, cy: cy, r: MARKER_RADIUS, fill: color }),
                React.createElement("text", { className: "quadrant-point-label", x: cx, y: above ? cy - MARKER_RADIUS - LABEL_GAP : cy + MARKER_RADIUS + LABEL_GAP, textAnchor: "middle", dominantBaseline: above ? "auto" : "hanging", fill: isActive ? color : theme.palette.text.primary, fontSize: 12, fontWeight: isActive ? 600 : 400 }, label)));
            if (label === point.name)
                return React.createElement(React.Fragment, { key: point.name }, marker);
            return (React.createElement(PeekTooltip, { key: point.name, title: React.createElement(ClusterTooltip, { members: [point], getPointColor: getPointColor, onHover: onPointHover, onClick: onPointClick }) }, marker));
        }),
        clusters.map((cluster, clusterIndex) => {
            const members = cluster.members.map((index) => points[index]);
            const color = dominantColor(members.map((member) => getPointColor(member)));
            const isActive = members.some((member) => member.name === activePointName);
            return (React.createElement("foreignObject", { key: members.map((member) => member.name).join("|"), x: cluster.cx - BADGE_BOX_W / 2, y: cluster.cy - BADGE_BOX_H / 2, width: BADGE_BOX_W, height: BADGE_BOX_H, style: { overflow: "visible" } },
                React.createElement("div", { style: { display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" } },
                    React.createElement(PeekTooltip, { arrow: true, placement: "top", title: React.createElement(ClusterTooltip, { members: members, getPointColor: getPointColor, onHover: onPointHover, onClick: onPointClick }) },
                        React.createElement("span", { style: { display: "inline-flex" } },
                            React.createElement(PeekChip, { className: "quadrant-cluster-badge", size: "small", label: onClusters ? labelFor(clusterIndex) : String(members.length), role: "button", tabIndex: 0, "aria-label": `${members.length} sources`, onClick: () => onPointClick === null || onPointClick === void 0 ? void 0 : onPointClick(members[0]), sx: {
                                    fontWeight: 700,
                                    cursor: "pointer",
                                    border: `2px solid ${theme.palette.common.white}`,
                                    boxShadow: isActive ? `0 0 0 2px ${color}` : theme.shadows[1],
                                    "&.MuiChip-root": { backgroundColor: `${color} !important` },
                                    "& .MuiChip-label": { color: `${theme.palette.common.white} !important`, px: 1 },
                                    "&:hover, &.MuiChip-clickable:hover, &:focus-visible": {
                                        backgroundColor: `${color} !important`,
                                        boxShadow: `0 0 0 2px ${color}`,
                                    },
                                } }))))));
        })));
};
export const QuadrantChart = ({ data, quadrants, xDomain, yDomain, splitX, splitY, height = 480, clusterRadiusPx = CLUSTER_RADIUS_PX, minClusterPoints = MIN_CLUSTER_POINTS, onClusters, lowLabel = "Low", highLabel = "High", positiveLabel = "+Gap", negativeLabel = "-Gap", activePointName, onPointHover, onPointClick, }) => {
    const theme = useTheme();
    const getPointColor = (point) => {
        var _a, _b;
        if (point.status) {
            const normalizedStatus = point.status
                .trim()
                .toLowerCase()
                .replace(/[\s_-]+/g, "-");
            const quadrant = quadrants.find((area) => area.id.toLowerCase() === normalizedStatus);
            if (quadrant) {
                return (_a = quadrant.titleColor) !== null && _a !== void 0 ? _a : theme.palette.primary.main;
            }
        }
        const quadrant = quadrants.find((area) => {
            const [xMin, xMax] = area.xRange;
            const [yMin, yMax] = area.yRange;
            return point.x >= xMin && point.x < xMax && point.y >= yMin && point.y < yMax;
        });
        return (_b = quadrant === null || quadrant === void 0 ? void 0 : quadrant.titleColor) !== null && _b !== void 0 ? _b : theme.palette.primary.main;
    };
    const CustomXAxisTick = ({ x, y, index }) => {
        var _a;
        const labels = [lowLabel, "", highLabel];
        const label = (_a = labels[index]) !== null && _a !== void 0 ? _a : "";
        return (React.createElement("text", { x: x, y: y, dy: 14, textAnchor: "middle", fill: theme.palette.grey[300], fontSize: 12 }, label));
    };
    const CustomYAxisTick = ({ x, y, payload }) => {
        let label = "";
        if ((payload === null || payload === void 0 ? void 0 : payload.value) === yDomain[1])
            label = positiveLabel;
        else if ((payload === null || payload === void 0 ? void 0 : payload.value) === splitY)
            label = "0";
        else if ((payload === null || payload === void 0 ? void 0 : payload.value) === yDomain[0])
            label = negativeLabel;
        return (React.createElement("text", { x: x - 8, y: y, dy: 4, textAnchor: "end", fill: theme.palette.grey[300], fontSize: 12 }, label));
    };
    return (React.createElement(ResponsiveContainer, { width: "100%", height: height },
        React.createElement(ScatterChart, { margin: { top: 30, right: 30, bottom: 40, left: 40 } },
            React.createElement(CartesianGrid, { strokeDasharray: "3 3", vertical: false }),
            React.createElement(XAxis, { type: "number", dataKey: "x", domain: xDomain, ticks: [xDomain[0], splitX, xDomain[1]], tick: React.createElement(CustomXAxisTick, null) }),
            React.createElement(YAxis, { type: "number", dataKey: "y", domain: yDomain, ticks: [yDomain[1], splitY, yDomain[0]], tick: React.createElement(CustomYAxisTick, null) }),
            quadrants.map((quadrant) => {
                var _a;
                return (React.createElement(ReferenceArea, { key: quadrant.id, x1: quadrant.xRange[0], x2: quadrant.xRange[1], y1: quadrant.yRange[0], y2: quadrant.yRange[1], fill: quadrant.fill, fillOpacity: 1, label: quadrant.tooltip
                        ? ({ viewBox }) => {
                            var _a;
                            const { x, y, width } = viewBox;
                            return (React.createElement("foreignObject", { x: x, y: y + 6, width: width, height: 30 },
                                React.createElement(PeekStack, { direction: "row", justifyContent: "center" },
                                    React.createElement(UnderlinedTooltip, { tooltip: quadrant.tooltip, sx: {
                                            color: (_a = quadrant.titleColor) !== null && _a !== void 0 ? _a : theme.palette.text.secondary,
                                            fontSize: 12,
                                            fontWeight: 500,
                                        } }, quadrant.title))));
                        }
                        : {
                            value: quadrant.title,
                            position: "insideTop",
                            offset: 10,
                            fill: (_a = quadrant.titleColor) !== null && _a !== void 0 ? _a : theme.palette.text.secondary,
                            fontSize: 12,
                            fontWeight: 500,
                        } }));
            }),
            React.createElement(ReferenceLine, { x: splitX, width: 10, stroke: "white" }),
            React.createElement(ReferenceLine, { y: splitY, width: 10, stroke: "white" }),
            React.createElement(Scatter, { data: data, isAnimationActive: false, shape: () => React.createElement("g", null) }),
            React.createElement(Customized, { component: React.createElement(ClusterLayer, { clusterRadiusPx: clusterRadiusPx, minClusterPoints: minClusterPoints, onClusters: onClusters, points: data, getPointColor: getPointColor, activePointName: activePointName, onPointHover: onPointHover, onPointClick: onPointClick }) }))));
};
export const QuadrantChartTitle = ({ children }) => (React.createElement(PeekTypography, { variant: "subtitle2", color: "text.secondary" }, children));
export default QuadrantChart;
